// Portions of this file are Copyright 2021 Google LLC, and licensed under GPL2+. See COPYING.
export const getParentDir = (path) => {
    let d = path.split('/').slice(0, -1).join('/');
    return d === '' ? (path.startsWith('/') ? '/' : '.') : d;
};
export function join(a, b) {
    if (a === '.')
        return b;
    if (a.endsWith('/'))
        return join(a.substring(0, a.length - 1), b);
    return b === '.' ? a : `${a}/${b}`;
}
/** Root under which all served model/library files live. */
export const LIBRARIES_ROOT = '/libraries';
// The worker is long-lived while the Emscripten instance is recreated per render,
// so cache the fetched tree at module scope: the network is hit once per context.
let treePromise;
function baseUrl() {
    // Resolve against self.location so this works on the main thread (page URL) and
    // inside the worker (worker script URL), and under a GitHub Pages subpath.
    return new URL('./libraries/', self.location.href).href;
}
export function fetchLibraryTree() {
    return treePromise ??= (async () => {
        const base = baseUrl();
        const index = await (await fetch(`${base}index.json`)).json();
        const filePaths = [];
        const dirChildren = new Map();
        const walk = (node, dir) => {
            const names = [];
            for (const [name, child] of Object.entries(node)) {
                names.push(name);
                const p = `${dir}/${name}`;
                if (child === null)
                    filePaths.push(p);
                else
                    walk(child, p);
            }
            dirChildren.set(dir, names);
        };
        walk(index, LIBRARIES_ROOT);
        dirChildren.set('/', [LIBRARIES_ROOT.substring(1)]);
        const entries = await Promise.all(filePaths.map(async (p) => {
            // p is /libraries/<...>; strip the leading /libraries/ for the URL.
            const rel = p.substring(LIBRARIES_ROOT.length + 1);
            const buf = await (await fetch(base + rel)).arrayBuffer();
            return [p, new Uint8Array(buf)];
        }));
        return { files: new Map(entries), dirChildren };
    })();
}
/**
 * A minimal synchronous, read-only FS over the fetched library tree, matching the
 * handful of methods the app actually calls (see the ambient FS type). Writes are
 * accepted into memory but not persisted; nothing on the main thread relies on them.
 */
export async function createEditorFS() {
    const { files, dirChildren } = await fetchLibraryTree();
    const isDir = (p) => dirChildren.has(p);
    return {
        readFileSync(path) {
            const data = files.get(path);
            if (!data)
                throw new Error(`ENOENT: no such file '${path}'`);
            return data;
        },
        readdirSync(path) {
            const names = dirChildren.get(path);
            if (!names)
                throw new Error(`ENOTDIR or ENOENT: '${path}'`);
            return names;
        },
        lstatSync(path) {
            const dir = isDir(path);
            if (!dir && !files.has(path))
                throw new Error(`ENOENT: '${path}'`);
            return { isDirectory: () => dir };
        },
    };
}
